import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import TimeSelector from './TimeSelector';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const StudentTimePreferencesDialog = ({ open, onClose, student, onSave }) => {
  const [dailyTimeSlots, setDailyTimeSlots] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Initialize daily time slots from student data
  useEffect(() => {
    if (student && student.time_preferences) {
      const slots = {};
      DAYS_OF_WEEK.forEach(day => {
        slots[day.value] = student.time_preferences
          .filter(pref => pref.day_of_week === day.value)
          .map(pref => ({
            id: pref.id,
            startTime: pref.start_time ? pref.start_time.substring(0, 5) : '', // Convert HH:MM:SS to HH:MM
            endTime: pref.end_time ? pref.end_time.substring(0, 5) : '', // Convert HH:MM:SS to HH:MM
            isPreferred: pref.is_preferred
          }));
      });
      setDailyTimeSlots(slots);
    } else {
      // Initialize empty slots
      const emptySlots = {};
      DAYS_OF_WEEK.forEach(day => {
        emptySlots[day.value] = [];
      });
      setDailyTimeSlots(emptySlots);
    }
  }, [student]);

  const handleAddTimeSlot = (dayOfWeek) => {
    setDailyTimeSlots(prev => ({
      ...prev,
      [dayOfWeek]: [
        ...prev[dayOfWeek],
        {
          id: `temp-${Date.now()}-${Math.random()}`,
          startTime: '',
          endTime: '',
          isPreferred: true
        }
      ]
    }));
  };

  const handleUpdateTimeSlot = (dayOfWeek, index, field, value) => {
    setDailyTimeSlots(prev => ({
      ...prev,
      [dayOfWeek]: prev[dayOfWeek].map((slot, i) =>
        i === index ? { ...slot, [field]: value } : slot
      )
    }));
  };

  const handleDeleteTimeSlot = (dayOfWeek, index) => {
    setDailyTimeSlots(prev => ({
      ...prev,
      [dayOfWeek]: prev[dayOfWeek].filter((_, i) => i !== index)
    }));
  };

  const validateTimeSlots = () => {
    for (const [day, slots] of Object.entries(dailyTimeSlots)) {
      if (slots.length === 0) continue;

      // Check for incomplete slots
      for (const slot of slots) {
        if (!slot.startTime || !slot.endTime) {
          setError('Please fill in all time slots');
          return false;
        }
        if (slot.startTime >= slot.endTime) {
          setError('End time must be after start time');
          return false;
        }
      }

      // Check for overlaps within same day
      if (slots.length > 1) {
        const sortedSlots = [...slots].sort((a, b) => a.startTime.localeCompare(b.startTime));
        for (let i = 0; i < sortedSlots.length - 1; i++) {
          if (sortedSlots[i].endTime > sortedSlots[i + 1].startTime) {
            setError(`Time slots overlap on ${DAYS_OF_WEEK[day].label}`);
            return false;
          }
        }
      }
    }

    // Check if at least one slot exists
    const totalSlots = Object.values(dailyTimeSlots).reduce((sum, slots) => sum + slots.length, 0);
    if (totalSlots === 0) {
      setError('At least one time slot is required');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    if (!validateTimeSlots()) {
      return;
    }

    setLoading(true);

    try {
             // Convert to backend format
       const timePreferences = [];
       Object.entries(dailyTimeSlots).forEach(([day, slots]) => {
         slots.forEach(slot => {
           // Only include slots that have both start and end times (not empty)
           if (slot.startTime && slot.endTime && slot.startTime.trim() !== '' && slot.endTime.trim() !== '') {
             timePreferences.push({
               dayOfWeek: parseInt(day),
               startTime: slot.startTime,
               endTime: slot.endTime,
               isPreferred: slot.isPreferred
             });
           }
         });
       });



       await onSave(timePreferences);
      setSuccess('Time preferences saved successfully!');
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      console.error('Error saving time preferences:', error);
      setError(error.response?.data?.message || 'Failed to save time preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ScheduleIcon />
          Student Time Preferences - {student?.first_name} {student?.last_name}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {student?.timezone && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              Timezone: {student.timezone}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={2}>
          {DAYS_OF_WEEK.map((day) => (
            <Grid item xs={12} key={day.value}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{day.label}</Typography>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => handleAddTimeSlot(day.value)}
                      size="small"
                    >
                      Add Time Slot
                    </Button>
                  </Box>

                  {dailyTimeSlots[day.value]?.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No time slots set for this day
                    </Typography>
                  ) : (
                    <Box>
                      {dailyTimeSlots[day.value]?.map((slot, index) => (
                        <Box key={slot.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={4}>
                              <TimeSelector
                                label="Start Time"
                                value={slot.startTime}
                                onChange={(value) => handleUpdateTimeSlot(day.value, index, 'startTime', value)}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TimeSelector
                                label="End Time"
                                value={slot.endTime}
                                onChange={(value) => handleUpdateTimeSlot(day.value, index, 'endTime', value)}
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <Chip
                                label={slot.isPreferred ? 'Preferred' : 'Available'}
                                color={slot.isPreferred ? 'primary' : 'default'}
                                size="small"
                                onClick={() => handleUpdateTimeSlot(day.value, index, 'isPreferred', !slot.isPreferred)}
                                style={{ cursor: 'pointer' }}
                              />
                            </Grid>
                            <Grid item xs={1}>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteTimeSlot(day.value, index)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : 'Save Preferences'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentTimePreferencesDialog;
